import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import DonationFormStripe from "./DonationFormStripe";

const EmbedDonationForm = () => {
  const { campaignId, orgSlug } = useParams();
  const containerRef = useRef(null);
  const normalizeRedirectUrl = (raw) => {
    const v = (raw || "").trim();
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v.replace(/^http:\/\//i, "https://");
    return `https://${v}`;
  };

  useEffect(() => {
    let lastSent = 0;
    let rafId = 0;

    const measure = () => {
      const el = containerRef.current;
      if (!el) return 0;

      // Measure only the container, not document/body to avoid loops.
      // Use both rect height and scrollHeight to cover overflowed children.
      const rectH = el.getBoundingClientRect().height || 0;
      const scrollH = el.scrollHeight || 0;
      const h = Math.max(rectH, scrollH);
      return Number.isFinite(h) ? Math.ceil(h) : 0;
    };

    const postHeightNow = () => {
      const h = measure();
      if (h > 0 && Math.abs(h - lastSent) > 1) {
        lastSent = h;
        window.parent?.postMessage(
          { source: "purelygive-embed", type: "height", height: h },
          "*",
        );
      }
    };

    const postNextPaint = () => {
      if (rafId) cancelAnimationFrame(rafId);
      // Two rAFs let layout/fonts settle without thrashing.
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(postHeightNow);
      });
    };

    // Observe size and DOM mutations within the container.
    const el = containerRef.current;
    let ro, mo;

    if (el && "ResizeObserver" in window) {
      ro = new ResizeObserver(postNextPaint);
      ro.observe(el);
    }
    if (el && "MutationObserver" in window) {
      mo = new MutationObserver(postNextPaint);
      mo.observe(el, { childList: true, subtree: true, attributes: true });
    }

    // Global layout-affecting events.
    const onResize = () => postNextPaint();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    // Initial boot: wait for fonts (if supported) and send a few times to catch late layout shifts.
    let canceled = false;
    const boot = async () => {
      try {
        if (document.fonts?.ready) await document.fonts.ready;
      } catch {}
      if (canceled) return;

      postNextPaint();

      const start = Date.now();
      const id = setInterval(() => {
        if (canceled) return clearInterval(id);
        postNextPaint();
        if (Date.now() - start > 1000) clearInterval(id);
      }, 200);
    };
    const bootTimer = setTimeout(boot, 30);

    return () => {
      canceled = true;
      clearTimeout(bootTimer);
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (ro && el) ro.unobserve(el);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        // Important: avoid viewport-coupled heights like minHeight: '100vh' or height: '100%'.
      }}
    >
      <DonationFormStripe
        campaignId={campaignId}
        orgSlug={orgSlug}
        hideCampaignDetails
        successRedirect={normalizeRedirectUrl(
          new URLSearchParams(window.location.search).get("redirect") || "",
        )}
      />
    </div>
  );
};

export default EmbedDonationForm;
